import type { LandingLocale } from '../../shared/content/landingContent.js';
import type { LandingEditorProject } from '../../shared/editor/landingEditorTypes.js';
import {
  inlineStyleMobileSize,
  normalizeInlineTextStyleList,
  type LandingEditorInlineTextStyle,
} from '../../shared/editor/landingEditorInlineTypography.js';
import { editorNodeIdClass } from './landingEditorRender.js';

const styleMarker = 'data-vp-inline-text-style';

const unwrapPreviousStyles = (root: ParentNode) => {
  root.querySelectorAll<HTMLElement>(`[${styleMarker}]`).forEach(node => {
    const parent = node.parentNode;
    if (!parent) return;
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    node.remove();
    parent.normalize();
  });
};

const textNodes = (root: HTMLElement) => {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node =>
      (node.parentElement?.closest('script, style, textarea, input') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
  return nodes;
};

const styleTextSlice = (root: HTMLElement, start: number, end: number, style: LandingEditorInlineTextStyle) => {
  let offset = 0;
  const slices = textNodes(root).flatMap(node => {
    const nodeStart = offset;
    const nodeEnd = nodeStart + node.data.length;
    offset = nodeEnd;
    const sliceStart = Math.max(start, nodeStart);
    const sliceEnd = Math.min(end, nodeEnd);
    return sliceStart < sliceEnd
      ? [{ node, start: sliceStart - nodeStart, end: sliceEnd - nodeStart }]
      : [];
  });
  slices.reverse().forEach(slice => {
    const after = slice.node.splitText(slice.end);
    const selected = slice.node.splitText(slice.start);
    const span = root.ownerDocument.createElement('span');
    span.setAttribute(styleMarker, 'true');
    span.className = 'vp-inline-text-style';
    if (style.highlight) span.classList.add('vp-inline-text-highlight');
    if (style.fontWeight !== undefined) span.style.fontWeight = String(style.fontWeight);
    if (style.color) span.style.color = style.color;
    if (style.fontSizeDesktop !== undefined)
      span.style.setProperty('--vp-inline-font-size-desktop', `${style.fontSizeDesktop}px`);
    const mobileSize = inlineStyleMobileSize(style);
    if (mobileSize !== undefined) span.style.setProperty('--vp-inline-font-size-mobile', `${mobileSize}px`);
    selected.parentNode?.insertBefore(span, selected);
    span.append(selected);
    void after;
  });
};

const syncNodeStyles = (node: HTMLElement, source: string, styles: LandingEditorInlineTextStyle[]) => {
  const content = node.textContent ?? '';
  const sourceOffset = content.indexOf(source);
  if (sourceOffset < 0) return;
  normalizeInlineTextStyleList(styles, source.length)
    .slice()
    .reverse()
    .forEach(style => styleTextSlice(node, sourceOffset + style.start, sourceOffset + style.end, style));
};

export const syncInlineTextTypography = (
  project: LandingEditorProject,
  locale: LandingLocale,
  root: ParentNode = document,
) => {
  unwrapPreviousStyles(root);
  for (const [elementId, frame] of Object.entries(project.elementFrames)) {
    const styles = frame.inlineTextStyles?.[locale];
    const source = frame.inlineTextSources?.[locale];
    if (!styles?.length || source === undefined) continue;
    root.querySelectorAll<HTMLElement>(`.${editorNodeIdClass(elementId)}`).forEach(node =>
      syncNodeStyles(node, source, styles),
    );
  }
};
